import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { SiteCopy, type SiteCopyKey } from "../copy";
import { ROUTES } from "../links";
import { HeroLine } from "./HeroLine";

/**
 * The homepage scroll story (the prototype's stInit, v38): a field of possible futures drawn on a
 * canvas, narrowing as you scroll, with Abi's plan picked out in gold and "You" moving along it,
 * under ten chapters of copy that turn like a vertical reel.
 *
 * Choreography (v37): something moves at every scroll position, never two things at once. The
 * field narrows while a sentence is held on screen and is still while the words hand over; a
 * sentence leaving keeps travelling up as the next rises in from below (v38).
 *
 * The field waves (v34/v35): the crowd is cached in interleaved layers (line i in layer i % GN),
 * each with its own wave, and blitted as vertical strips offset by the wave at their x. The wave
 * grows with the square of the distance from the anchor, so the gold dot never moves, and damps to
 * nothing before the gold line draws. Performance (docs/BACKEND-HANDOFF.md): strip widths are 1:1
 * in device pixels, only the band the field occupies is cleared and blitted, the wave clock runs
 * at 30 Hz so the idle guard can hold still frames, the settled field is flattened into one bitmap
 * on first use, and the loop stops when the story leaves the viewport or the tab is hidden (zero
 * canvas work off screen). With `prefers-reduced-motion` there is no loop at all: the chapters are
 * laid out as plain sections (CSS) and the canvas is hidden.
 *
 * Every tunable is in STORY_FIELD.
 */
export const STORY_FIELD = {
  /** below this window width the phone values apply (read once, at mount) */
  phoneBelow: 760,
  /** lines in the crowd, and points per line */
  lines: { desktop: 100, phone: 64 },
  points: { desktop: 90, phone: 48 },
  /** interleaved cache layers, each with its own wave: the per-frame cost is per layer */
  layers: { desktop: 5, phone: 2 },
  /** vertical strips each layer is sliced into to carry its wave */
  strips: { desktop: 40, phone: 26 },
  /** the far end's wave amplitude, px */
  amplitude: { desktop: 3.9, phone: 2.8 },
  /** the share of a line's length over which the wave ramps up (squared) from the anchor */
  waveRamp: 0.24,
  /** the wave clock, Hz */
  waveHz: 30,
  /** how far a sentence travels as it rises in or out of the middle, px */
  carousel: { desktop: 52, phone: 34 },
  /** the canvas's device-pixel-ratio cap */
  maxDpr: 1.5,
  /** the narrowing bands, in chapters of scroll (u) */
  bands: [
    [0, 0.58],
    [0.93, 1.58],
    [1.93, 2.58],
    [2.93, 3.58],
  ] as [number, number][],
  /** the field's spread and opacity, before and after narrowing */
  spread: [1.75, 0.3] as [number, number],
  alpha: [0.34, 0.26] as [number, number],
  /** chapter c arrives over [c + in0, c + in1] and leaves over [c + out0, c + out1] */
  textIn: [-0.26, -0.07] as [number, number],
  textOut: [0.58, 0.74] as [number, number],
  /** the wave damps to nothing over this range of u, before the gold line */
  calm: [2.95, 3.9] as [number, number],
  /** the gold line draws, then "You" travels along it */
  gold: [4.04, 4.62] as [number, number],
  you: [4.95, 9.55] as [number, number],
  /** the first-load draw-in, seconds */
  introSeconds: 2.4,
};

const CHAPTERS: { h: SiteCopyKey; p?: SiteCopyKey; first?: boolean; last?: boolean }[] = [
  { h: "home.story.s1", first: true },
  { h: "home.story.s2" },
  { h: "home.story.s3" },
  { h: "home.story.s4", p: "home.story.s4p" },
  { h: "home.story.s5" },
  { h: "home.story.s6", p: "home.story.s6p" },
  { h: "home.story.s7", p: "home.story.s7p" },
  { h: "home.story.s8", p: "home.story.s8p" },
  { h: "home.story.s9" },
  { h: "home.story.s10", p: "home.story.s10p", last: true },
];

export function ScrollStory({ skipTo }: { skipTo: string }) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const sec = sectionRef.current;
    if (!sec) return;
    return startStory(sec);
  }, []);

  const skip = () => {
    const el = document.getElementById(skipTo);
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <section className="st" id="st" aria-label="What Abi does" ref={sectionRef}>
      <div className="st-stick">
        <canvas className="st-cv" aria-hidden="true" />
        {CHAPTERS.map((c, i) => (
          <div className="st-ch" data-ch={i} aria-hidden="true" key={c.h}>
            {c.first ? <HeroLine /> : <SiteCopy as="h2" k={c.h} />}
            {c.p && <SiteCopy as="p" k={c.p} />}
            {c.first && (
              <div className="st-scroll">
                <SiteCopy k="home.story.scroll" />
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 4.5v13" />
                  <path d="M6.4 12.2 12 17.8l5.6-5.6" />
                </svg>
              </div>
            )}
            {c.last && (
              <>
                <Link className="st-cta" to={ROUTES.start}>
                  <SiteCopy k="home.story.cta" />
                </Link>
                <Link className="st-emp" to={ROUTES.employers}>
                  <SiteCopy k="home.story.emp" />
                </Link>
              </>
            )}
          </div>
        ))}
        <div className="st-dots" aria-hidden="true">
          {CHAPTERS.map((c) => (
            <i key={c.h} />
          ))}
        </div>
      </div>
      {/* One bar fixed to the viewport, shown while the story is on screen (v34): hung off the
          sticky block, the way out started below the fold. */}
      <div className="st-bar">
        <SiteCopy as="p" className="st-fine" k="home.story.fine" />
        <button type="button" className="st-skip" onClick={skip}>
          Skip to the product
        </button>
      </div>
    </section>
  );
}

/**
 * A chapter's opacity and vertical offset at scroll position `u` (in chapters): the reel. A
 * sentence arrives as the one before finishes leaving; leaving, it keeps travelling up, and its
 * successor rises into the middle from below.
 */
export function chapterPose(
  c: number,
  count: number,
  u: number,
  intro: number,
  travel: number,
): { o: number; dy: number } {
  const [i0, i1] = STORY_FIELD.textIn;
  const [o0, o1] = STORY_FIELD.textOut;
  const fin = (c === 0 ? 1 : sm(c + i0, c + i1, u)) * (c === 0 ? intro : 1);
  const fout = c === count - 1 ? 1 : 1 - sm(c + o0, c + o1, u);
  const dy = fout < 1 ? -(1 - fout) * travel : (1 - fin) * travel;
  return { o: Math.min(fin, fout), dy };
}

const cl = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const sm = (a: number, b: number, v: number) => {
  const t = cl((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const lp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * The wave's vertical offset for layer wave `w` at `x` (0 at the anchor, 1 at the right edge) and
 * clock `bt` (seconds). Zero at the anchor by construction: the ramp is squared.
 */
export function waveOffset(
  w: { a: number; k: number; s: number; p: number },
  x: number,
  bt: number,
  calm: number,
  amp: number,
): number {
  if (calm <= 0 || x <= 0) return 0;
  const R = STORY_FIELD.waveRamp;
  const r = x >= R ? 1 : (x / R) * (x / R);
  return calm * r * amp * w.a * Math.sin(x * w.k - bt * w.s + w.p);
}

/** Wires the story to `sec`; returns the cleanup. A port of the prototype's stInit (v38). */
export function startStory(sec: HTMLElement): () => void {
  const cv = sec.querySelector<HTMLCanvasElement>(".st-cv");
  const cx = cv?.getContext("2d");
  const chs = [...sec.querySelectorAll<HTMLElement>(".st-ch")];
  const dotsEl = sec.querySelector<HTMLElement>(".st-dots");
  const bar = sec.querySelector<HTMLElement>(".st-bar");
  const stick = sec.querySelector<HTMLElement>(".st-stick");
  if (!cv || !cx || !dotsEl || !stick) return () => undefined;
  const CH = chs.length;
  const dots = [...dotsEl.children] as HTMLElement[];
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  if (reduce) {
    chs.forEach((el) => {
      el.style.opacity = "";
      el.style.transform = "";
      el.style.visibility = "";
      el.style.pointerEvents = "";
      el.setAttribute("aria-hidden", "false");
      el.inert = false;
    });
    dotsEl.style.display = "none";
    bar?.classList.add("on");
    return () => undefined;
  }

  const F = STORY_FIELD;
  const phone = window.innerWidth < F.phoneBelow;
  const pick = <T,>(v: { desktop: T; phone: T }) => (phone ? v.phone : v.desktop);
  const CARO = pick(F.carousel);
  const N = pick(F.lines);
  const M = pick(F.points);
  const WAMP = pick(F.amplitude);
  const GN = pick(F.layers);
  const SN = pick(F.strips);
  let sd = 20261018;
  const rnd = () => {
    sd = (Math.imul(sd, 1664525) + 1013904223) >>> 0;
    return sd / 4294967296;
  };
  const nrm = () => {
    let u = 0;
    for (let i = 0; i < 6; i++) u += rnd();
    return (u - 3) * 1.41;
  };
  const paths: number[][] = [];
  const sstep = 0.125 * (90 / M);
  for (let i = 0; i < N; i++) {
    const a = [0];
    let y = 0;
    const dr = nrm() * 0.02;
    for (let k = 1; k <= M; k++) {
      y += nrm() * sstep + dr;
      a.push(y);
    }
    paths.push(a);
  }
  // The plan is one of the same jagged lines, drawn by the same code. It is steadier than the
  // crowd and finishes a little above the best of them: that is the claim being made.
  const gi = 0;
  {
    let top = -1e9;
    for (let i = 1; i < N; i++) if (paths[i][M] > top) top = paths[i][M];
    const end = top * 1.07;
    const a = [0];
    let y = 0;
    for (let k = 1; k <= M; k++) {
      y += nrm() * sstep * 0.62 + end / M;
      a.push(y);
    }
    const corr = end - a[M];
    for (let k = 1; k <= M; k++) a[k] += corr * Math.pow(k / M, 1.6);
    paths[0] = a;
  }
  const comp = (v: number) => 2.1 * Math.tanh(v / 2.1);
  let W = 0;
  let H = 0;
  let dpr = 1;
  let lastH = 0;
  let lastSpread = -1;
  let lastGold = -1;
  let lastYou = -1;
  let lastBt = -1;
  let run = true;
  let raf = 0;
  const t0 = performance.now();
  // The crowd depends only on spread, so it is cached (in GN interleaved layers) and blitted.
  const offs: HTMLCanvasElement[] = [];
  const oxs: CanvasRenderingContext2D[] = [];
  for (let g = 0; g < GN; g++) {
    const c = document.createElement("canvas");
    const x = c.getContext("2d");
    if (!x) return () => undefined;
    offs.push(c);
    oxs.push(x);
  }
  // Once the field has settled the layers are flattened into one bitmap, built on first use.
  const flat = document.createElement("canvas");
  const fx = flat.getContext("2d");
  if (!fx) return () => undefined;
  let flatOk = false;
  // Each layer's own wave, seeded from the same generator, so the field is the same every load.
  const GW: { a: number; k: number; s: number; p: number }[] = [];
  // The band the futures occupy: comp() caps at 2.1, so nothing is drawn above BY0.
  let BY0 = 0;
  let BH = 0;

  function size() {
    dpr = Math.min(F.maxDpr, window.devicePixelRatio || 1);
    W = cv!.clientWidth;
    H = cv!.clientHeight;
    cv!.width = Math.round(W * dpr);
    cv!.height = Math.round(H * dpr);
    cx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    const oy0 = H * (phone ? 0.76 : 0.72);
    const sc0 = H * (phone ? 0.11 : 0.16);
    BY0 = Math.max(0, oy0 - sc0 * 2.2 - 30);
    BH = H - BY0;
    for (let g = 0; g < GN; g++) {
      offs[g].width = cv!.width;
      offs[g].height = Math.round(BH * dpr);
      oxs[g].setTransform(dpr, 0, 0, dpr, 0, -BY0 * dpr);
    }
    flat.width = cv!.width;
    flat.height = Math.round(BH * dpr);
    flatOk = false;
    lastSpread = -1;
    lastGold = -1;
    lastYou = -1;
    lastBt = -1;
  }
  for (let g = 0; g < GN; g++) {
    GW.push({
      a: 0.74 + 0.52 * rnd(),
      k: 4.1 + 3.6 * rnd(),
      s: 0.95 + 0.95 * rnd(),
      p: rnd() * 6.2832,
    });
  }
  size();
  lastH = H;
  // iOS collapses its toolbar mid-scroll, which fires resize. Ignore height-only changes.
  const onResize = () => {
    if (cv.clientWidth === W && Math.abs(cv.clientHeight - lastH) < 90) return;
    lastH = cv.clientHeight;
    size();
  };
  window.addEventListener("resize", onResize, { passive: true });

  const prog = () => {
    const r = sec.getBoundingClientRect();
    return cl(-r.top / (r.height - window.innerHeight));
  };
  function paintText(u: number, intro: number) {
    chs.forEach((el, c) => {
      const { o, dy } = chapterPose(c, CH, u, intro, CARO);
      el.style.opacity = String(o);
      el.style.transform = `translateY(${dy.toFixed(2)}px)`;
      el.style.visibility = o > 0.02 ? "visible" : "hidden";
      el.style.pointerEvents = o > 0.9 ? "auto" : "none";
      el.setAttribute("aria-hidden", o > 0.9 ? "false" : "true");
      el.inert = !(o > 0.9);
    });
    const cur = cl(Math.round(u), 0, CH - 1);
    dots.forEach((d, i) => d.classList.toggle("on", i === cur));
  }

  function frame() {
    raf = 0;
    if (!run) return;
    const p = prog();
    const u = p * CH * 0.9999;
    const steps = F.bands.reduce((a, b) => a + sm(b[0], b[1], u), 0) / F.bands.length;
    const spread = lp(F.spread[0], F.spread[1], steps);
    const alpha = lp(F.alpha[0], F.alpha[1], steps);
    // The wave clock ticks at waveHz, so a still scroll can hold whole frames.
    const bt = Math.floor((performance.now() * F.waveHz) / 1000) / F.waveHz;
    const calm = 1 - sm(F.calm[0], F.calm[1], u);
    const gold = sm(F.gold[0], F.gold[1], u);
    const you = sm(F.you[0], F.you[1], u);
    const intro = sm(0, F.introSeconds, (performance.now() - t0) / 1000);
    paintText(u, intro);
    const x0 = W * 0.05;
    const x1 = W;
    const oy = H * (phone ? 0.76 : 0.72);
    const sc = H * (phone ? 0.11 : 0.16);
    const Y = (i: number, k: number) => oy - comp(paths[i][k] * spread) * sc;
    const c = cx!;
    // On first load every future draws itself in from the left, on the clock, bypassing the cache.
    if (intro < 1) {
      const reveal = intro * M;
      c.clearRect(0, 0, W, H);
      c.lineJoin = "round";
      c.lineCap = "round";
      c.lineWidth = 1.1;
      for (let i = 0; i < N; i++) {
        c.beginPath();
        for (let k = 0; k <= M; k++) {
          if (k > reveal) break;
          const xx = lp(x0, x1, k / M);
          const yy = Y(i, k);
          if (k) c.lineTo(xx, yy);
          else c.moveTo(xx, yy);
        }
        c.strokeStyle = "#7FA1E8";
        c.globalAlpha = cl(alpha * (0.55 + (0.9 * ((i * 37) % 11)) / 11), 0, 0.9);
        c.stroke();
      }
      c.globalAlpha = 1;
      c.fillStyle = "#F2B90D";
      c.beginPath();
      c.arc(x0, oy, 6 * intro, 0, 7);
      c.fill();
      lastSpread = -1;
      raf = requestAnimationFrame(frame);
      return;
    }
    let rebuilt = false;
    if (Math.abs(spread - lastSpread) > 0.0015) {
      rebuilt = true;
      lastSpread = spread;
      for (const o of oxs) {
        o.clearRect(0, BY0, W, BH);
        o.lineJoin = "round";
        o.lineCap = "round";
        o.lineWidth = 1.1;
        o.strokeStyle = "#7FA1E8";
      }
      for (let i = 0; i < N; i++) {
        if (i === gi) continue;
        const o = oxs[i % GN];
        o.beginPath();
        for (let k = 0; k <= M; k++) {
          const xx = lp(x0, x1, k / M);
          const yy = Y(i, k);
          if (k) o.lineTo(xx, yy);
          else o.moveTo(xx, yy);
        }
        o.globalAlpha = cl(0.34 * (0.55 + (0.9 * ((i * 37) % 11)) / 11), 0, 0.9);
        o.stroke();
      }
      for (const o of oxs) o.globalAlpha = 1;
      // Only read once the wave has damped out, after the narrowing: built on first use.
      flatOk = false;
    }
    const still =
      Math.abs(gold - lastGold) <= 0.0008 &&
      Math.abs(you - lastYou) <= 0.0008 &&
      bt === lastBt &&
      !rebuilt;
    if (still) {
      raf = requestAnimationFrame(frame);
      return;
    }
    lastGold = gold;
    lastYou = you;
    lastBt = bt;
    c.clearRect(0, BY0, W, BH);
    const wv = (g: number, x: number) => waveOffset(GW[g], x, bt, calm, WAMP);
    c.globalAlpha = alpha / 0.34;
    const gw = offs[0].width;
    const gh = offs[0].height;
    if (calm <= 0.002) {
      if (!flatOk) {
        fx!.setTransform(1, 0, 0, 1, 0, 0);
        fx!.clearRect(0, 0, flat.width, flat.height);
        for (const o of offs) fx!.drawImage(o, 0, 0);
        flatOk = true;
      }
      c.drawImage(flat, 0, 0, gw, gh, 0, BY0, W, BH);
    } else {
      // Strip widths are exactly 1:1 in device pixels (a fractional width resamples the strip
      // and halves the frame rate on a throttled phone); the vertical offset stays fractional so
      // the seams don't show as a staircase along the field's flat top.
      const sw = gw / SN;
      for (let g = 0; g < GN; g++) {
        for (let i = 0; i < SN; i++) {
          const sx = Math.round(i * sw);
          const ssw = Math.min(Math.round((i + 1) * sw) - sx + 1, gw - sx);
          const xc = sx / dpr;
          c.drawImage(
            offs[g],
            sx,
            0,
            ssw,
            gh,
            xc,
            BY0 + wv(g, (xc - x0) / (x1 - x0)),
            ssw / dpr,
            BH,
          );
        }
      }
    }
    c.globalAlpha = 1;
    // The live lines ride the same wave, sampled per point.
    const YS = (i: number, k: number) => Y(i, k) + wv(0, (lp(x0, x1, k / M) - x0) / (x1 - x0));
    c.lineJoin = "round";
    c.lineCap = "round";
    c.beginPath();
    for (let k = 0; k <= M; k++) {
      const xx = lp(x0, x1, k / M);
      const yy = YS(gi, k);
      if (k) c.lineTo(xx, yy);
      else c.moveTo(xx, yy);
    }
    c.strokeStyle = "#7FA1E8";
    c.globalAlpha = cl(alpha * (1 - gold) * 1.2, 0, 0.9);
    c.lineWidth = 1.1;
    c.stroke();
    if (gold > 0.001) {
      const gk = Math.max(1, Math.floor(M * gold));
      c.save();
      c.shadowColor = "#F2B90D";
      c.shadowBlur = 18 * gold;
      c.beginPath();
      for (let k = 0; k <= gk; k++) {
        const xx = lp(x0, x1, k / M);
        const yy = YS(gi, k);
        if (k) c.lineTo(xx, yy);
        else c.moveTo(xx, yy);
      }
      c.strokeStyle = "#F2B90D";
      c.globalAlpha = 1;
      c.lineWidth = 3.2;
      c.stroke();
      c.restore();
    }
    // the anchor: the wave is zero there by construction, so it never moves
    c.globalAlpha = 1;
    c.fillStyle = "#F2B90D";
    c.beginPath();
    c.arc(x0, oy, 6, 0, 7);
    c.fill();
    // you, moving along your own plan, from "your priorities" to the end
    if (you > 0.001) {
      const k = Math.min(M, Math.round(M * (0.07 + 0.86 * you)));
      const xx = lp(x0, x1, k / M);
      const yy = YS(gi, k);
      c.strokeStyle = "rgba(255,255,255,.55)";
      c.lineWidth = 1.4;
      c.beginPath();
      c.arc(xx, yy, 13, 0, 7);
      c.stroke();
      c.fillStyle = "#fff";
      c.beginPath();
      c.arc(xx, yy, 6.5, 0, 7);
      c.fill();
      c.font = "600 13px Inter,system-ui,sans-serif";
      c.textAlign = xx > W * 0.82 ? "right" : "center";
      c.fillText("You", xx, yy + 30);
      c.textAlign = "left";
    }
    raf = requestAnimationFrame(frame);
  }
  function start() {
    run = true;
    if (!raf) raf = requestAnimationFrame(frame);
  }
  function stop() {
    run = false;
    if (raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  }
  // Stop the loop when the story is off screen or the tab is hidden; the bar shows with the story.
  let io: IntersectionObserver | null = null;
  if ("IntersectionObserver" in window) {
    io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          bar?.classList.toggle("on", e.isIntersecting);
          if (e.isIntersecting) start();
          else stop();
        }),
      { threshold: 0 },
    );
    io.observe(stick);
  } else {
    bar?.classList.add("on");
  }
  const onVis = () => (document.hidden ? stop() : start());
  document.addEventListener("visibilitychange", onVis);
  start();

  return () => {
    stop();
    io?.disconnect();
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("resize", onResize);
  };
}
